/**
 * The HTTP routes the server exposes and the clients call.
 *
 * Shared constants rather than string literals on each side, so a renamed route
 * is a compile error in both modules instead of a 404 discovered in a browser.
 *
 * @see LunaPinApi
 */
package se.soderbjorn.lunapin.clientserver

/**
 * How a caller identifies itself, on every route below.
 *
 * **Two carriers, one lookup.** The browser presents the session as the `HttpOnly`
 * cookie the server set (see the server's `Sessions.kt`); a caller with no cookie jar —
 * the Android and iOS apps — presents the same opaque id as `Authorization: Bearer
 * <sessionId>`. Both are read by one function on the server, so there is one trust
 * decision rather than two, only one of which would stay exercised.
 *
 * The header wins when both arrive. Neither carrier may ever be a query parameter: a
 * session id is a bearer credential for the whole account, and query strings end up in
 * access logs, referrer headers, browser history and pasted URLs.
 */
object ApiRoutes {
    /**
     * `GET` — who this client is acting as, as a [SessionState].
     *
     * **Never 401s.** The shell mounts before the session has resolved and asks
     * this on the way, so "nobody" has to be an answer rather than a failure. A
     * null [SessionState.user] is therefore an ordinary response and every client
     * renders it; the route has no anonymous-caller failure mode at all.
     */
    const val SESSION: String = "/api/session"

    /**
     * `POST` — sign in, from a [SignInRequest] carrying the username and password
     * this deployment was configured with. Answers a [SignInResponse] and sets the
     * session cookie.
     *
     * The cookie is set unconditionally, for every caller. A mobile client ignores it,
     * and that is cheaper than a branch. What is *not* unconditional is the session id in
     * the body: it is populated only for a caller that set
     * [SignInRequest.wantsSessionId], because a browser handed a copy of its own
     * `HttpOnly` cookie in a readable field has lost the only thing that attribute was
     * protecting.
     *
     * **The only way in there is.** LunaPin serves exactly one account, whose
     * credentials come from the operator's environment (`LUNAPIN_USERNAME` and
     * `LUNAPIN_PASSWORD`) rather than from a registration flow — so there is no
     * sign-up route, no password-change route, and nothing here that creates a
     * second account.
     *
     * Three refusals, and the first two are deliberately the *same* refusal:
     *  - **401** for a wrong password.
     *  - **401** for a wrong username, with an identical status and message. A
     *    caller must not be able to learn that a username exists — that turns one
     *    guessing problem into two easier ones.
     *  - **503** when this server has no credentials configured, because that is a
     *    deployment problem rather than a bad guess, and a 401 would send the
     *    operator looking for a typo in a password that is not being read at all.
     *
     * And one throttle:
     *  - **429**, with `Retry-After`, once a caller has failed a handful of times.
     *    See the server's `LoginRateLimiter` for exactly what that does and does
     *    not protect against.
     *
     * Paired with [AUTH_SIGN_OUT] by name on purpose: the two doors of a single
     * account should read as two doors, not as a verb and a noun.
     */
    const val AUTH_SIGN_IN: String = "/api/auth/signin"

    /**
     * `POST` — end this device's session and clear the cookie. 204, and safe to call
     * when already signed out.
     *
     * **This device's**, precisely: sessions are per-device, so signing out of the
     * laptop leaves the phone signed in.
     */
    const val AUTH_SIGN_OUT: String = "/api/auth/signout"

    /**
     * `GET` — every shell setting stored for the caller, as a [UiSettingsState].
     * `POST` — store one of them, from a [SetUiSettingRequest]; 204 on success.
     *
     * This is the route behind "my tabs and windows are still there on the next
     * machine". The values are lunula's own persistence blobs plus LunaPin's
     * workspace blob, stored verbatim; which keys may travel is an allowlist, see
     * [UiSettingKeys].
     */
    const val USER_UI_SETTINGS: String = "/api/user/ui-settings"

    /**
     * `GET` — what this account has chosen, as an [AccountSettings].
     * `PUT` — store all of it, from a [SetAccountSettingsRequest]; answers the stored
     * [AccountSettings].
     *
     * Both **401 for an anonymous caller**, like [BOOKMARKS] and for the same reason: a
     * preference belongs to an account, and there is no honest answer without one.
     *
     * **An account that has never written gets the defaults, not a 404.** "Never chose"
     * and "chose the default" are one state here — see [AccountSettings], which
     * contrasts it with [UiSettingsState.settings], where they deliberately are not.
     *
     * `PUT` rather than `POST`, and the whole set rather than one field, because this is
     * a form somebody applies: the request states what the settings are, so repeating it
     * is a no-op and a half-applied set is not reachable. Contrast [USER_UI_SETTINGS],
     * which is a `POST` of one key at a time because the toolkit writes that way.
     *
     * Refusals carry the store's own sentence, naming the field and what it will take —
     * a tag count outside 0–10, a summary length or a language that is not one of the
     * values [SummaryLengths] and [SummaryLanguages] list.
     *
     * **Nothing here returns the model provider key, or any part of it.** The GET
     * answers [AccountSettings.aiAvailable] — a bare boolean — and there is no route,
     * here or anywhere, that answers with more. The key is the operator's
     * `LUNAPIN_KEY_OPENAI`, read at boot; see the server's `AiConfig`.
     */
    const val USER_SETTINGS: String = "/api/user/settings"

    /**
     * `GET` — everything this account has saved, as a [BookmarkList], newest first.
     * `POST` — save one, from a [SaveBookmarkRequest]; answers the stored [Bookmark].
     *
     * Both 401 for an anonymous caller. Unlike [SESSION] there is no honest answer
     * for one: a bookmark belongs to an account, and there is no account.
     *
     * The POST is an upsert on the URL rather than a create, so it is safe to repeat
     * — which matters because the thing calling it most is a bookmarklet somebody
     * may press twice on the same page. It answers 200 with the stored row rather
     * than 201, because the caller cannot know which of the two it did and does not
     * need to.
     *
     * `GET ?tag=rust` narrows to one tag, case-insensitively. That is the one query
     * the storage was shaped for — tags are rows, not a comma-separated column, so
     * this is an index seek rather than a scan of the whole collection.
     *
     * **No paging, and no free-text search.** Both are real and neither is guessed at
     * here: the shape of a query is decided by the surface that runs it, and there is
     * no such surface yet. [BookmarkList] is a wrapper so that adding one is a field
     * rather than a new response type.
     */
    const val BOOKMARKS: String = "/api/bookmarks"

    /**
     * `GET /api/bookmarks/{id}` — one row; answers the stored [Bookmark].
     * `PUT /api/bookmarks/{id}` — edit one, from an [EditBookmarkRequest]; answers the
     * stored [Bookmark].
     * `DELETE /api/bookmarks/{id}` — forget one. 204, and 404 for an id this account
     * does not own.
     *
     * **404 rather than 403 for somebody else's row**, which is the same answer as
     * for a row that does not exist. With one account per deployment that
     * distinction is theoretical today; stating it now is what keeps it from being
     * decided by accident on the day it is not. All three verbs obey it, from one scoped
     * query rather than from a second question about ownership.
     *
     * The `GET` is the newest of the three and was deliberately absent: an editor found its
     * bookmark in the collection [BOOKMARKS] answers, on the grounds that the collection is
     * fetched whole anyway and a route per row would be a second way to read one table. What
     * ended that is scale rather than taste — reading one bookmark out of a few thousand
     * meant serialising a few thousand — and it is the same pressure that will eventually put
     * paging on [BOOKMARKS]. Adding it changes nothing about the other two: same scoped
     * query, same 404.
     *
     * The `PUT` is where a field can be *cleared*, which the [BOOKMARKS] `POST` cannot
     * do — see [EditBookmarkRequest], which is where that difference is argued. Its
     * refusals are 400 carrying the server's own sentence, including the one for moving
     * a bookmark onto a URL this account has already saved.
     *
     * A template rather than a function, because the two callers build it
     * differently — Ktor mounts the pattern, a client interpolates the id.
     */
    const val BOOKMARK_BY_ID: String = "/api/bookmarks/{id}"

    /**
     * `POST /api/bookmarks/{id}/summarise` — read this page again, summarise it and tag
     * it. Answers the stored [Bookmark], whose [Bookmark.aiState] is now
     * [AiStates.PENDING].
     *
     * The edit window's *Regenerate*, and the *Try again* on a row that failed. Both are
     * the same request because they are the same act: a summary that came back wrong, a
     * page that was behind a login the first time and is not now, and a site that was down
     * are one problem — ask again — and giving each its own route would be three ways to
     * say it.
     *
     * `POST` because it mutates: the bookmark enters a queue and its state changes. It
     * answers immediately and the summary arrives later, exactly as a save does — nothing
     * on this server blocks a request on a model.
     *
     * **Not idempotent in the useful sense**, and deliberately so: asking twice queues it
     * twice in the sense that the second ask resets the attempt count and the position.
     * That is what *Try again* on a bookmark that has already failed three times has to
     * mean, and it is why `enqueueForAi` clears `ai_attempts`.
     *
     * **404 for an id this account does not own**, identical to the answer for one that
     * does not exist — see [BOOKMARK_BY_ID], where that rule is argued. A malformed id is
     * the same 404.
     *
     * Refused, before the id is even looked at, when this deployment has no model provider
     * key (**503**) or this account has turned both AI switches off (**409**). Each carries
     * a sentence saying which of the two it is; see [SummariseMissingResult]'s file, where
     * the pair is argued. Silently queueing work that can never run would be the one
     * outcome nobody can debug.
     *
     * A template rather than a function, because the two callers build it differently —
     * Ktor mounts the pattern, a client interpolates the id.
     */
    const val BOOKMARK_SUMMARISE: String = "/api/bookmarks/{id}/summarise"

    /**
     * `POST` — put every bookmark that has *never been tried* in the enrichment queue, and
     * answer how many that was, as a [SummariseMissingResult].
     *
     * The backfill: an account that has just been given a key has a collection from before
     * the feature existed, and there is otherwise no way to ask about it except re-saving
     * every link by hand.
     *
     * **Never tried, not merely un-summarised**, and the difference is the whole design of
     * this route. A bookmark whose page 404s has no summary and never will; including it
     * would make this a retry-everything button that spends somebody's quota re-reading
     * pages that already failed for good reasons. Asking again about *that* row is
     * [BOOKMARK_SUMMARISE], which is a press about one bookmark.
     *
     * **Not throttled here.** It queues four hundred rows in one statement and returns; the
     * worker's own pacing — roughly twenty a minute — is what keeps a backfill from being
     * an afternoon at full tilt against a metered API. A route that throttled as well would
     * be a second opinion about a rate that is already decided.
     *
     * Refused for [BOOKMARK_SUMMARISE]'s two reasons, with the same two statuses and the
     * same kind of sentence.
     *
     * How many it *would* enqueue, before it is pressed, is [AiUsage.awaitingSummary] on
     * [USER_AI_USAGE] — so a button can be labelled *Summarise 412 older bookmarks* rather
     * than *Backfill*. What became of it once it has been pressed is [AI_PROGRESS], and
     * [AI_CANCEL_BACKFILL] is how it is stopped.
     */
    const val BOOKMARKS_SUMMARISE_MISSING: String = "/api/bookmarks/summarise-missing"

    /**
     * `GET` — how far this account's enrichment queue has got, as an [EnrichmentProgress].
     *
     * What the backfill panel polls, roughly every two seconds, while a settings window is
     * open. It is the other half of [BOOKMARKS_SUMMARISE_MISSING]: that one starts hours of
     * work and answers a number once, and this is how anybody finds out what became of it.
     *
     * ── Polled, and deliberately not pushed down [EVENTS] ────────────────────
     *
     * The broker publishes *a bookmark changed*, not *the queue is this long*. A settings
     * window subscribed to it would be recounting on every one of thousands of events — a
     * backfill of four thousand bookmarks is four thousand broadcasts — to redraw one number
     * that nobody is watching to the second. Polling costs one query every two seconds and
     * only while the pane that draws it is open, which is the cheaper end of that trade by
     * some distance.
     *
     * **401 for an anonymous caller**, like [USER_AI_USAGE] and for its reason: a queue length
     * is a statement about an account.
     *
     * **Never refused for the two reasons [BOOKMARK_SUMMARISE] is refused.** A deployment with
     * no key and an account with both switches off can both still have rows in the queue —
     * from before the key was removed, from before the switches were flicked — and counting
     * them is an honest answer in every configuration. A 503 here would blank a panel that has
     * something true to say.
     *
     * Under `/api/ai/` beside [AI_TEST] rather than under `/api/bookmarks/`: what it describes
     * is the enrichment queue rather than the collection. The rows it counts happen to be
     * bookmarks because that is where the queue lives — see the server's `Bookmarks.sq`, which
     * argues why the queue is four columns rather than a table.
     */
    const val AI_PROGRESS: String = "/api/ai/progress"

    /**
     * `POST` — take everything this account has queued back out of the queue, answering a
     * [CancelBackfillResult]. The brakes on [BOOKMARKS_SUMMARISE_MISSING].
     *
     * **It does not touch the worker.** One `UPDATE` moves this account's `pending` rows to
     * `none`; the worker asks the database what is next and finds nothing. There is no flag,
     * no cooperative cancellation and no signalling between this route and a coroutine — see
     * `cancelPendingForAi`, where that and the choice of `none` are argued.
     *
     * **The one `running` row is left alone.** It finishes in seconds and its model call is
     * already paid for.
     *
     * **No confirmation, anywhere.** Cancelling is undone by pressing Backfill again — the
     * cancelled rows are untried, so they are counted and re-queued exactly as before — which
     * makes this the rare destructive-sounding button that destroys nothing.
     *
     * Refused only for being anonymous, and not for [AI_PROGRESS]'s two non-reasons plus one
     * of its own: **an account that has just turned both AI switches off is precisely the
     * account most likely to want its queue emptied**, so refusing it with a 409 would leave
     * somebody unable to stop work they have just decided they do not want.
     *
     * Idempotent in the way that matters: a second press finds nothing pending and answers
     * zero.
     */
    const val AI_CANCEL_BACKFILL: String = "/api/ai/cancel"

    /**
     * `GET` — how much this account has asked the model for this month, as an [AiUsage].
     *
     * **401 for an anonymous caller**, like [USER_SETTINGS] and for its reason: a usage
     * count belongs to an account, and a stranger has not named one.
     *
     * Under `/api/user/` rather than `/api/bookmarks/` because it is a fact about the
     * account rather than about the collection — the same reason [USER_SETTINGS] is there —
     * and because the one screen that draws it is the settings screen.
     *
     * An account that has enriched nothing this month gets four zeroes, not a 404. "Never
     * had any" and "had none" are one state here, exactly as they are for [USER_SETTINGS].
     */
    const val USER_AI_USAGE: String = "/api/user/ai-usage"

    /**
     * `POST` — make one minimal real call to the model provider and say what came back,
     * as an [AiTestResult].
     *
     * The settings screen's *Test connection*. It answers the question the pill cannot:
     * [AccountSettings.aiAvailable] says a key is *set*, and this says whether the key that
     * is set *works* — a revoked one, a typo, a model name that no longer exists and an
     * exhausted balance all look exactly like a working deployment until something is
     * asked.
     *
     * **200 for a test that ran**, whichever way it went — a rejected key is a successful
     * test with a sentence to show, and [AiTestResult.ok] is where that difference lives.
     * See [AiTestResult], where the choice is argued. Three statuses mean *no call was
     * made*:
     *
     *  - **401** for an anonymous caller, like [USER_SETTINGS] and for its reason.
     *  - **503** when this deployment has no key, matching [BOOKMARK_SUMMARISE]'s refusal
     *    for the same state. Refusing is the point: a call that cannot work would report a
     *    provider failure for what is a missing variable.
     *  - **429**, with `Retry-After`, once a caller has pressed it several times in a few
     *    minutes. Unlike every other route here **each call spends the operator's money**,
     *    so the throttle counts successes as well as failures; see the server's
     *    `AiTestRateLimiter`.
     *
     * **The account's AI switches are deliberately not consulted**, which is where this
     * parts company with [BOOKMARK_SUMMARISE]'s 409. Those switches say what should happen
     * when a bookmark is saved; this asks whether the deployment's key works, which is a
     * fact about the deployment. An operator checking a key they have just installed
     * should not be refused by a preference belonging to the account.
     *
     * **Nothing it answers carries the key, or any part of it** — see [AiTestResult].
     */
    const val AI_TEST: String = "/api/ai/test"

    /**
     * `GET` — every tag this account uses, as a list of [TagUse]: the tag, how many
     * bookmarks carry it, and whether it is one the model coined.
     *
     * **401 for an anonymous caller**, like [BOOKMARKS] and for the same reason: a tag
     * list is a statement about a collection, and a stranger has not named one.
     *
     * Ordered most-used first, with the name breaking ties, and that order is the
     * server's answer rather than a hint — it is what every `#tag` menu draws in, and a
     * client that re-sorted would be showing a different list from the one the next
     * client shows. [TagInputRules] says why the count is the point.
     *
     * **A bare array rather than a wrapper**, which is the one place this differs from
     * [BOOKMARKS] on purpose. [BookmarkList] is a wrapper so that paging can arrive as a
     * field; this cannot need paging, because it is one row per distinct tag over one
     * account's collection and it is already an aggregate of everything there is.
     *
     * [TagUse.fromAi] is derived from the tag rows on every read rather than stored —
     * see the query in `Bookmarks.sq`, which is where that argument belongs.
     */
    const val TAGS: String = "/api/tags"

    /**
     * `GET` — what the Vocabulary panel says about this account's tags, as a [TidyStatus]:
     * how many there are, how many are used once, how many bookmarks they file, and whether
     * the last tidy can still be undone.
     *
     * The idle half of the tidy tool, and a route of its own rather than a field on [TAGS]
     * for two reasons. [TAGS] is a bare array by design — it is one row per tag and cannot
     * grow a header — and this answers a question about the *tidying*, including whether a
     * snapshot exists, which nothing about a tag list knows.
     *
     * **401 for an anonymous caller**, like [TAGS] and for its reason.
     *
     * Cheap and safe to poll, though nothing does: two counts and a lookup, no model, no key.
     */
    const val TAGS_TIDY: String = "/api/tags/tidy"

    /**
     * `POST` — work out what this account's vocabulary could be tidied into, as a [TidyPlan].
     *
     * ── The deterministic passes run first, and the model sees what is left ──
     *
     * Singular/plural pairs and singletons are found by rule, server-side, against the live
     * tag rows. Only what survives them is described to the model — on the measured collection
     * that is roughly 470 real tags instead of 1182 mostly-noise ones, which is both cheaper
     * and markedly better advice, because a list full of tags used once teaches a model that
     * hyper-specific tags are the house style.
     *
     * **It still works with no key.** The deterministic half is SQL and string handling; a
     * deployment with no `LUNAPIN_KEY_OPENAI` gets a plan with [TidyPlan.modelConsulted] false
     * and a smaller set of proposals, rather than the 503 [BOOKMARK_SUMMARISE] answers. There
     * is deliberately no special case for the unconfigured server anywhere in this feature.
     *
     * **Nothing is applied.** This is the read half; [TAGS_TIDY_APPLY] is the write half, and
     * the plan travels back through the caller so that what is applied is what was shown.
     *
     * **429, with `Retry-After`, once it has been pressed several times in a few minutes.**
     * Unlike almost everything else on this server a press can spend the operator's money, so
     * the throttle counts calls rather than failures — the same counter shape [AI_TEST] uses,
     * and for the same reason. A separate allowance from that one: testing a key and planning
     * a tidy are two buttons, and spending one should not lock the other.
     *
     * **401 for an anonymous caller.** A vocabulary belongs to an account.
     */
    const val TAGS_TIDY_PLAN: String = "/api/tags/tidy/plan"

    /**
     * `POST` — what this [TidySelection] would do, as a [TidyImpact]. Changes nothing.
     *
     * What a dialog asks after somebody unticks a row. The counts cannot be recomputed by a
     * client: the number of bookmarks left with no tags at all needs every bookmark's tag rows,
     * and a client holding those would be holding the collection in order to draw a summary.
     *
     * **The same request type [TAGS_TIDY_APPLY] takes**, which is what makes "the preview
     * described what the button did" a property of the shape rather than a thing to remember.
     * It runs the same validation too, so a selection that cannot be applied previews as the
     * repaired one — a merge into a tag nobody has becomes a drop here exactly as it would
     * there, and the number shown is the number that would happen.
     *
     * No model, no key, no throttle: it is a grouped read of one account's tag rows.
     *
     * **401 for an anonymous caller.**
     */
    const val TAGS_TIDY_PREVIEW: String = "/api/tags/tidy/preview"

    /**
     * `POST` — apply this [TidySelection], answering a [TidyResult]. **Destructive.**
     *
     * The whole of it runs in one transaction, and the account's tag rows are snapshotted
     * inside that same transaction before anything is written — so there is no instant at
     * which a tidy has half happened, and none at which it has happened with nothing to go
     * back to. [TAGS_TIDY_UNDO] is the way back, and it is a single step: one snapshot,
     * replaced each time, not a history.
     *
     * **No second confirmation is expected in front of this, and none should be added.** The
     * dialog already showed a preview computed by [TAGS_TIDY_PREVIEW] against these exact
     * numbers, and undo is the safety net. A confirmation over a previewed, reversible action
     * is a dialog people learn to dismiss without reading, and the next one is the one that
     * mattered.
     *
     * **The selection is re-validated here**, against the live vocabulary rather than against
     * whatever a client was shown: a `from` that is not a tag this account owns is dropped from
     * the plan, a `to` that does not survive becomes a drop, chains and cycles are refused
     * outright, and a change that would leave a bookmark with no tags at all is refused. That
     * is true of this project's own dialog as much as of a script — the server cannot tell them
     * apart and must not need to.
     *
     * **400 with a sentence** for a selection that cannot be repaired into a coherent plan — a
     * chain (`a→b` where `b→c`) or a cycle. Those are refused rather than half-applied, because
     * either resolution is a guess about what somebody meant.
     *
     * **401 for an anonymous caller.**
     *
     * It sweeps the account's own tag lists too: `saveQuickTags` and `saveDefaultTags` hold raw
     * tag strings, so a merge or a delete that left them alone would leave a quick-tag chip
     * pointing at a tag that no longer exists.
     */
    const val TAGS_TIDY_APPLY: String = "/api/tags/tidy/apply"

    /**
     * `POST` — put the account's tags back as they were before the last tidy, answering an
     * [UndoTidyResult].
     *
     * **One step, and the snapshot is spent by taking it.** Undoing twice does nothing the
     * second time and answers zero, which is an ordinary answer rather than a failure.
     *
     * It is what makes the tool usable rather than frightening: with a way back, *try 100,
     * look at it, undo, try 150* is the natural way to find the right ceiling. Without one,
     * the honest dialog has to be alarming enough that nobody presses it.
     *
     * The account's two tag lists are restored with the rows, because the apply rewrote them.
     *
     * **A bookmark deleted since the tidy is not resurrected**, and its snapshot rows went with
     * it by the same cascade that removed its live ones — see the server's migration. So an undo
     * restores the tags of the bookmarks that still exist, which is the only thing it could mean.
     *
     * **401 for an anonymous caller.**
     */
    const val TAGS_TIDY_UNDO: String = "/api/tags/tidy/undo"

    /**
     * `GET` — a **server-sent event stream** of [ServerEvent]s for the caller's account,
     * held open until the client goes away.
     *
     * The one route on this server that does not answer and finish. Everything else here is a
     * question with an answer; this is a subscription, and it is what retired the `↻` button.
     *
     * ── SSE rather than a websocket ──────────────────────────────────────────
     *
     * Nothing ever travels client→server on this channel, because every mutation already has a
     * REST route above. That removes the entire reason a websocket exists here, and with it
     * the upgrade negotiation to get wrong behind a proxy — Lunamux's equivalent is a
     * websocket precisely because its commands *do* go up it. What is left wants one-way
     * streaming with reconnect built into the protocol, which browsers speak natively through
     * `EventSource` and Ktor speaks on every other target.
     *
     * ── Two carriers, one lookup, and why that matters here ──────────────────
     *
     * [readPresentedSessionId] already reads either the browser's cookie or the phone's
     * `Authorization: Bearer`, so this route authenticates both with code that exists. That is
     * unusually convenient rather than lucky: **`EventSource` cannot set headers**, which
     * would be a problem for a bearer-only server and is not one here, because the browser is
     * the cookie carrier and the phones — which use Ktor's client and can set what they like
     * — are the bearer carriers.
     *
     * **401 for an anonymous caller**, like [BOOKMARKS] and for its reason: a stream of an
     * account's changes is a statement about an account.
     *
     * ── What it carries ──────────────────────────────────────────────────────
     *
     * Identity, not payload, and every event names the client that caused it so nobody re-reads
     * because of their own write — see [ServerEvent], where both choices are argued. Plus a
     * periodic heartbeat comment, which is not decoration: an idle connection with nothing on
     * it is exactly what a proxy or a mobile radio drops quietly, and the symptom of dropping
     * it is a client that looks connected and hears nothing.
     *
     * **No replay, and no `Last-Event-ID`.** A client that reconnects has missed whatever was
     * said while it was away, so it re-reads on *every* successful connect rather than only
     * the first. That is a smaller and more honest mechanism than a server-side backlog: "go
     * and read the current truth" cannot be stale, where a replayed backlog can be both
     * incomplete and out of date.
     */
    const val EVENTS: String = "/api/events"

    /**
     * `GET` — the Connections tab, as an [McpState]: the switch, the URL to paste into an
     * agent, and what is connected.
     *
     * **Session-authenticated like every other route here, and pointedly unlike the
     * `/oauth` group and `/mcp`.** Those are unauthenticated because they are how an agent
     * *obtains* a credential; this is where the person who granted one looks at what they
     * granted.
     *
     * **401 for an anonymous caller**, like [BOOKMARKS] and for a sharper version of its
     * reason: an empty [McpState] would be indistinguishable from an account with nothing
     * connected, and the tab would draw a switch nobody can use.
     *
     * [McpState.serverUrl] is built here from the origin the request arrived on. A client
     * must not assemble it — see [McpState].
     */
    const val MCP: String = "/api/mcp"

    /**
     * `POST` — turn agent access on or off, from an [McpEnabledRequest]; answers the whole
     * [McpState].
     *
     * Takes the **desired state** rather than meaning "toggle", so a double click or a
     * retry lands on one answer rather than either. See [McpEnabledRequest].
     *
     * **Off is a gate, not a purge.** Nothing is revoked and no token is invalidated: both
     * gates re-read the flag per request, so every agent stops within one request and works
     * again the moment it is switched back on, with no second trip through a browser.
     * [MCP_CONNECTION_BY_ID] is what deletes. Conflating the two would mean somebody who
     * switched it off to think about it came back to find every connection gone.
     *
     * **401 for an anonymous caller**, like [MCP]. There is no 403 here: LunaPin has one
     * account and no administrator, so there is no second permission to be refused by.
     */
    const val MCP_ENABLED: String = "/api/mcp/enabled"

    /**
     * `DELETE /api/mcp/connections/{clientId}` — cut one agent off; answers the whole
     * [McpState].
     *
     * The destructive half of this tab, and the only one: it deletes that agent's tokens,
     * so its very next call 401s and it must go through the browser flow again to come
     * back. Other connections are untouched.
     *
     * **Silent on a `clientId` this account has nothing for**, answering the same 200 and
     * the same state. The requested end state is "that agent cannot act as me", and it
     * already holds; a different answer would also make this a probe for which
     * registrations exist.
     *
     * **401 for an anonymous caller**, like [MCP].
     *
     * A template rather than a function, because the two callers build it differently —
     * Ktor mounts the pattern, a client interpolates the id. See [mcpConnection].
     */
    const val MCP_CONNECTION_BY_ID: String = "/api/mcp/connections/{clientId}"

    /** [BOOKMARK_BY_ID] for one row. */
    fun bookmarkById(id: Long): String = "/api/bookmarks/$id"

    /**
     * [MCP_CONNECTION_BY_ID] for one agent.
     *
     * Interpolated without escaping, and safe to be: a client id is minted by this server
     * as a fixed prefix and thirty-two hex characters — see the server's `OAuthCrypto` — so
     * there is no value here a registration could put a path separator into.
     */
    fun mcpConnection(clientId: String): String = "/api/mcp/connections/$clientId"

    /** [BOOKMARK_SUMMARISE] for one row. */
    fun bookmarkSummarise(id: Long): String = "/api/bookmarks/$id/summarise"
}
